import type { FieldType } from "@/types/template";

/** Map FieldType → TypeScript type annotation for the Props interface. */
export function tsTypeForField(type: FieldType): string {
  switch (type) {
    case "text":
    case "textarea":
    case "color":
    case "color-alpha":
    case "image":
    case "select":
      return "string";
    case "number":
    case "slider":
    case "knob":
      return "number";
    case "boolean":
      return "boolean";
    case "text-rich":
      // bold/italic/underline are optional whole-text formatting flags.
      // Templates should treat undefined as false (don't apply the style).
      // Range-based formatting is a future iteration — for now these apply
      // to the whole string.
      return "{ text: string; font: string; fontSize: number; bold?: boolean; italic?: boolean; underline?: boolean }";
    default:
      return "string";
  }
}

/**
 * Insert a new prop into the TSX source code:
 * 1. Adds `key: type;` to the Props interface
 * 2. Adds `key` to the function's destructured params
 *
 * Returns the patched code. No-op if the key already exists in the interface.
 */
export function insertFieldIntoCode(
  code: string,
  key: string,
  fieldType: FieldType,
): string {
  // Skip if the key already exists in the Props interface
  if (new RegExp(`\\b${key}\\s*:`).test(code)) {
    return code;
  }

  const tsType = tsTypeForField(fieldType);
  let result = code;

  // 1. Find the Props interface and add the prop before the closing brace
  const interfaceMatch = result.match(
    /(interface\s+Props\s*\{)([\s\S]*?)(\n\})/,
  );
  if (interfaceMatch) {
    const [full, opening, body, closing] = interfaceMatch;
    result = result.replace(
      full!,
      `${opening}${body}  ${key}: ${tsType};\n${closing}`,
    );
  }

  // 2. Find the function's destructured props and add the key
  const destructureMatch = result.match(
    /(\{\s*(?:[\w,\s]+)\s*\})(\s*:\s*Props\s*\))/,
  );
  if (destructureMatch) {
    const [full, params, suffix] = destructureMatch;
    const trimmed = params!.trimEnd();
    const withoutBrace = trimmed.slice(0, -1).trimEnd();
    const needsComma = withoutBrace.length > 1 && !withoutBrace.endsWith(",");
    result = result.replace(
      full!,
      `${withoutBrace}${needsComma ? "," : ""} ${key} }${suffix}`,
    );
  }

  return result;
}

/**
 * Remove a prop from the TSX source code:
 * 1. Removes `key: type;` from the Props interface
 * 2. Removes `key` from the function's destructured params
 */
export function removeFieldFromCode(code: string, key: string): string {
  let result = code;

  // Remove from Props interface: matches "  key: type;\n" or "  key: type;"
  result = result.replace(new RegExp(`\\s*${key}\\s*:\\s*\\w+;\\n?`), "\n");

  // Remove from destructured params
  // Handles: ", key" or "key, " or "key"
  result = result.replace(new RegExp(`,\\s*${key}\\b`), "");
  result = result.replace(new RegExp(`${key}\\s*,\\s*`), "");

  return result;
}
